import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api, { TokenStorage, baseURL } from '../utils/api';
import zxcvbn from 'zxcvbn';
import { Eye, EyeOff } from 'lucide-react';
import AuthShell from '../components/common/AuthShell';
import { Button, FormField, Input } from '../components/ui';

const strengthLabels = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'];

const scoreToBarClass = (s) => {
  if (s === null || s === undefined) return 'bg-gray-200';
  if (s <= 1) return 'bg-red-500';
  if (s === 2) return 'bg-amber-500';
  if (s === 3) return 'bg-yellow-400';
  return 'bg-green-500';
};

export default function SignUp() {
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [pwScore, setPwScore] = useState(null);
  const [pwFeedback, setPwFeedback] = useState({ warning: '', suggestions: [] });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  const { email: watchedEmail, password: watchedPassword } = formData;
  useEffect(() => {
    const trimmed = (watchedPassword || '').trim();
    if (!trimmed) {
      setPwScore(null);
      setPwFeedback({ warning: '', suggestions: [] });
      return;
    }
    const inputs = [];
    if (watchedEmail) {
      inputs.push(watchedEmail);
      inputs.push(watchedEmail.split('@')[0]);
    }
    const result = zxcvbn(trimmed, inputs);
    setPwScore(result.score);
    setPwFeedback({
      warning: result.feedback.warning || '',
      suggestions: result.feedback.suggestions || [],
    });
  }, [watchedEmail, watchedPassword]);

  const handleChange = (e) => {
    setError('');
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const passwordMeetsPolicy = () => {
    const { email, password } = formData;
    if (!password || password.length < 12) return false;
    if (pwScore < 3) return false;

    const local = (email || '').split('@')[0] || '';
    const lowerPw = password.toLowerCase();
    if (email && lowerPw.includes(email.toLowerCase())) return false;
    if (local && lowerPw.includes(local.toLowerCase())) return false;

    return true;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    const { firstName, email, password, confirmPassword } = formData;
    setError('');

    if (!firstName.trim()) {
      setError('First name is required.');
      return;
    }
    if (!email || !password || !confirmPassword) {
      setError('Email and password fields are required.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Invalid email address.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!passwordMeetsPolicy()) {
      setError('Please choose a stronger password (12+ chars, hard to guess).');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/api/auth/signup', {
        email,
        password,
        confirmPassword,
        timezone: browserTz,
        firstName: firstName.trim(),
        lastName: formData.lastName.trim() || undefined,
      });
      TokenStorage.setToken(res.data.accessToken);
      try {
        sessionStorage.setItem('userId', res.data.userId);
        sessionStorage.setItem('firstName', res.data.firstName || firstName.trim());
      } catch (err) {
        // private browsing — non-critical
      }
      navigate('/welcome');
    } catch (err) {
      const msg = err.response?.data?.error || 'Signup failed.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleOAuth = () => {
    window.location.href = `${baseURL}/api/auth/google`;
  };

  const canSubmit =
    formData.firstName.trim().length > 0 &&
    passwordMeetsPolicy() &&
    formData.password === formData.confirmPassword &&
    !loading;

  return (
    <AuthShell title="Create Account" subtitle="Sign up to get started">
      {error && (
        <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <div className="font-semibold">Sign Up Failed</div>
          <p className="mt-1">{error}</p>
        </div>
      )}

      <form onSubmit={handleSignUp} className="space-y-4 sm:space-y-5" noValidate>
        <FormField
          label="First Name"
          type="text"
          name="firstName"
          placeholder="First name"
          value={formData.firstName}
          onChange={handleChange}
          required
          autoComplete="given-name"
        />

        <FormField
          label={
            <>
              Last Name <span className="text-gray-400 font-normal">(optional)</span>
            </>
          }
          type="text"
          name="lastName"
          placeholder="Last name"
          value={formData.lastName}
          onChange={handleChange}
          autoComplete="family-name"
        />

        <FormField
          label="Email Address"
          type="email"
          name="email"
          placeholder="you@securethebag.ai"
          value={formData.email}
          onChange={handleChange}
          required
          autoComplete="email"
        />

        {/* Password requirements */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
          <div className="text-base sm:text-sm font-semibold text-gray-900 mb-2">
            Password requirements
          </div>
          <ul className="text-base sm:text-sm text-gray-700 space-y-1 list-disc list-inside">
            <li>Minimum <strong>12 characters</strong></li>
            <li>Aim for <strong>Strong</strong> or better rating</li>
            <li>Use letters, numbers, and symbols</li>
            <li>Don't include your email address</li>
            <li>Use a unique password</li>
          </ul>
        </div>

        {/* Password */}
        <div>
          <label htmlFor="signup-password" className="block text-base sm:text-sm font-medium text-gray-700 mb-1.5">
            Password
          </label>
          <div className="relative">
            <Input
              id="signup-password"
              type={showPw ? 'text' : 'password'}
              name="password"
              placeholder="Enter password"
              value={formData.password}
              onChange={handleChange}
              required
              autoComplete="new-password"
              className="pr-12"
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/40"
              aria-label={showPw ? 'Hide password' : 'Show password'}
            >
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {pwScore !== null && (
            <div className="mt-2">
              <div
                className="h-2 bg-gray-200 rounded-full overflow-hidden"
                aria-live="polite"
              >
                <div
                  className={`h-full transition-all duration-300 ${scoreToBarClass(pwScore)}`}
                  style={{ width: `${(pwScore + 1) * 20}%` }}
                />
              </div>
              <div className="mt-1 text-sm text-gray-600">{strengthLabels[pwScore]}</div>
              {pwFeedback.warning && (
                <div className="mt-1 text-sm text-red-700">{pwFeedback.warning}</div>
              )}
              {pwFeedback.suggestions?.length > 0 && (
                <ul className="mt-1 text-sm text-gray-600 list-disc list-inside">
                  {pwFeedback.suggestions.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="signup-confirm-password" className="block text-base sm:text-sm font-medium text-gray-700 mb-1.5">
            Confirm Password
          </label>
          <div className="relative">
            <Input
              id="signup-confirm-password"
              type={showPw2 ? 'text' : 'password'}
              name="confirmPassword"
              placeholder="Re-enter password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              autoComplete="new-password"
              className="pr-12"
            />
            <button
              type="button"
              onClick={() => setShowPw2((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/40"
              aria-label={showPw2 ? 'Hide confirm password' : 'Show confirm password'}
            >
              {showPw2 ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <p className="text-xs text-gray-500 text-center">
          By creating an account, you agree to our{' '}
          <Link to="/terms" className="text-blue-700 hover:underline">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-blue-700 hover:underline">
            Privacy Policy
          </Link>
          .
        </p>

        <Button type="submit" variant="primary" fullWidth disabled={!canSubmit} loading={loading}>
          {loading ? 'Creating Account' : 'Create Account'}
        </Button>

        <div className="flex items-center gap-4 my-4">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-sm text-gray-500">or</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        <Button type="button" variant="secondary" fullWidth onClick={handleGoogleOAuth}>
          Sign up with Google
        </Button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-base sm:text-sm text-gray-600">
          Already have an account?{' '}
          <Link
            to="/login"
            className="text-blue-700 hover:text-blue-800 font-medium transition-colors inline-flex items-center min-h-[44px] sm:min-h-0"
          >
            Log in
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
